import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Workflow, Plus, Trash2, Play, Pause, Zap, CheckCircle2, Bell, Hash, Clock,
  Tag, ArrowRight, ChevronDown, ChevronUp, FileText, BarChart3, Settings2,
  LayoutTemplate, TestTube2
} from 'lucide-react';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const TRIGGER_TYPES = {
  post_created: { label: 'Post Created', icon: FileText, description: 'When a new post is created' },
  post_scheduled: { label: 'Post Scheduled', icon: Clock, description: 'When a post is scheduled' },
  post_published: { label: 'Post Published', icon: CheckCircle2, description: 'When a post is published' },
  keyword_match: { label: 'Keyword Match', icon: Hash, description: 'When post contains specific keywords' },
  engagement_threshold: { label: 'Engagement Threshold', icon: BarChart3, description: 'When engagement exceeds a threshold' },
};

const ACTION_TYPES = {
  auto_approve: { label: 'Auto-approve', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
  add_hashtags: { label: 'Add Hashtags', icon: Hash, color: 'text-blue-600 bg-blue-50' },
  notify_team: { label: 'Notify Team', icon: Bell, color: 'text-amber-600 bg-amber-50' },
  change_status: { label: 'Change Status', icon: Settings2, color: 'text-violet-600 bg-violet-50' },
  add_label: { label: 'Add Label', icon: Tag, color: 'text-pink-600 bg-pink-50' },
};

const CATEGORY_COLORS = {
  approval: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  content: 'bg-blue-50 text-blue-700 border-blue-200',
  notification: 'bg-amber-50 text-amber-700 border-amber-200',
  scheduling: 'bg-violet-50 text-violet-700 border-violet-200',
  monitoring: 'bg-pink-50 text-pink-700 border-pink-200',
  organization: 'bg-slate-50 text-slate-700 border-slate-200',
};

// ========== Workflow Card ==========
const WorkflowCard = ({ workflow, onToggle, onDelete, onTest, onView }) => {
  const trigger = TRIGGER_TYPES[workflow.trigger?.type] || { label: workflow.trigger?.type, icon: Zap };
  const TriggerIcon = trigger.icon;

  return (
    <Card className={`transition-all ${workflow.is_active ? 'border-slate-200' : 'border-slate-100 opacity-70'}`} data-testid="workflow-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800 truncate">{workflow.name}</h3>
              <Badge variant={workflow.is_active ? 'default' : 'outline'} className={`text-[10px] ${workflow.is_active ? 'bg-emerald-500' : ''}`}>
                {workflow.is_active ? 'Active' : 'Paused'}
              </Badge>
            </div>
            {workflow.description && <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{workflow.description}</p>}
          </div>
          <Switch checked={workflow.is_active} onCheckedChange={() => onToggle(workflow.workflow_id)} data-testid="workflow-toggle" />
        </div>

        {/* Trigger -> Actions flow */}
        <div className="flex items-center gap-2 mb-3 bg-slate-50 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 text-xs">
            <TriggerIcon className="h-3.5 w-3.5 text-violet-500" />
            <span className="font-medium text-slate-600">{trigger.label}</span>
          </div>
          <ArrowRight className="h-3 w-3 text-slate-300 flex-shrink-0" />
          <div className="flex gap-1.5 flex-wrap">
            {(workflow.actions || []).map((action, i) => {
              const actionCfg = ACTION_TYPES[action.type] || { label: action.type, icon: Zap, color: 'text-slate-600 bg-slate-50' };
              const ActionIcon = actionCfg.icon;
              return (
                <span key={`${action.type}-${i}`} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${actionCfg.color}`}>
                  <ActionIcon className="h-3 w-3" />{actionCfg.label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-400">
            {workflow.execution_count || 0} executions
            {workflow.last_executed_at && ` · Last: ${new Date(workflow.last_executed_at).toLocaleDateString()}`}
          </span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onTest(workflow.workflow_id)} data-testid="test-workflow-btn">
              <TestTube2 className="h-3 w-3 mr-1" />Test
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-600" onClick={() => onDelete(workflow.workflow_id)} data-testid="delete-workflow-btn">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ========== Create Workflow Dialog ==========
const CreateWorkflowDialog = ({ open, onClose, onCreated, templates }) => {
  const [tab, setTab] = useState('template');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState('post_created');
  const [triggerConditions, setTriggerConditions] = useState('');
  const [actions, setActions] = useState([{ type: 'auto_approve', params: {} }]);
  const [loading, setLoading] = useState(false);

  const handleCreateFromTemplate = async (template) => {
    setLoading(true);
    try {
      const res = await api.post('/workflows/', {
        name: template.name,
        description: template.description,
        trigger: template.trigger,
        actions: template.actions,
        is_active: true,
      });
      toast.success(`Workflow "${res.data.name}" created`);
      onCreated(res.data);
      onClose();
    } catch (e) {
      if (!isUpgradeError(e)) toast.error(e.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustom = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      let conditions = {};
      if (triggerConditions.trim()) {
        try { conditions = JSON.parse(triggerConditions); } catch { conditions = { value: triggerConditions }; }
      }
      const res = await api.post('/workflows/', {
        name, description,
        trigger: { type: triggerType, conditions },
        actions,
        is_active: true,
      });
      toast.success(`Workflow "${res.data.name}" created`);
      onCreated(res.data);
      setName(''); setDescription(''); onClose();
    } catch (e) {
      if (!isUpgradeError(e)) toast.error(e.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setLoading(false);
    }
  };

  const addAction = () => setActions(prev => [...prev, { type: 'notify_team', params: {} }]);
  const removeAction = (idx) => setActions(prev => prev.filter((_, i) => i !== idx));
  const updateActionType = (idx, type) => setActions(prev => prev.map((a, i) => i === idx ? { type, params: {} } : a));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="create-workflow-dialog">
        <DialogHeader>
          <DialogTitle>Create Workflow</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="template" className="flex-1">From Template</TabsTrigger>
            <TabsTrigger value="custom" className="flex-1">Custom</TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="space-y-3 mt-3">
            {(templates || []).map(t => (
              <div key={t.template_id} className="p-3 border border-slate-200 rounded-xl hover:border-violet-300 transition-colors cursor-pointer" onClick={() => handleCreateFromTemplate(t)} data-testid="workflow-template">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-800">{t.name}</span>
                  <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[t.category] || CATEGORY_COLORS.organization}`}>{t.category}</Badge>
                </div>
                <p className="text-xs text-slate-500">{t.description}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="custom" className="space-y-4 mt-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="My Workflow" className="mt-1" data-testid="workflow-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Trigger</label>
              <Select value={triggerType} onValueChange={setTriggerType}>
                <SelectTrigger className="mt-1" data-testid="trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TRIGGER_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label} — {v.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center justify-between">
                Actions
                <Button size="sm" variant="ghost" className="text-xs h-6" onClick={addAction}>
                  <Plus className="h-3 w-3 mr-1" />Add
                </Button>
              </label>
              <div className="space-y-2 mt-1">
                {actions.map((action, i) => (
                  <div key={`action-${action.type}-${i}`} className="flex items-center gap-2">
                    <Select value={action.type} onValueChange={v => updateActionType(i, v)}>
                      <SelectTrigger className="flex-1" data-testid={`action-select-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ACTION_TYPES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {actions.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => removeAction(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleCreateCustom} disabled={loading} data-testid="create-workflow-btn">
                {loading ? 'Creating...' : 'Create Workflow'}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ========== Main Page ==========
export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('workflows');

  const fetchData = useCallback(async () => {
    try {
      const [wfRes, tplRes, statsRes, logsRes] = await Promise.all([
        api.get('/workflows/'),
        api.get('/workflows/templates'),
        api.get('/workflows/stats/summary'),
        api.get('/workflows/logs/recent'),
      ]);
      setWorkflows(wfRes.data);
      setTemplates(tplRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (workflowId) => {
    try {
      const res = await api.post(`/workflows/${workflowId}/toggle`);
      setWorkflows(prev => prev.map(w => w.workflow_id === workflowId ? { ...w, is_active: res.data.is_active } : w));
      toast.success(res.data.is_active ? 'Workflow activated' : 'Workflow paused');
    } catch (e) {
      toast.error('Failed to toggle workflow');
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm('Delete this workflow?')) return;
    try {
      await api.delete(`/workflows/${workflowId}`);
      setWorkflows(prev => prev.filter(w => w.workflow_id !== workflowId));
      toast.success('Workflow deleted');
    } catch (e) {
      toast.error('Failed to delete workflow');
    }
  };

  const handleTest = async (workflowId) => {
    try {
      const res = await api.post(`/workflows/${workflowId}/test`);
      const results = res.data.test_results || [];
      toast.success(`Test complete: ${results.length} action(s) simulated`);
      fetchData();
    } catch (e) {
      toast.error('Test failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="workflows-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Automated Workflows</h1>
            <p className="text-sm text-slate-500 mt-1">Create rules to automate your social media management</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="add-workflow-btn">
            <Plus className="h-4 w-4 mr-2" />New Workflow
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_workflows}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Active</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.active_workflows}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Executions</p>
              <p className="text-2xl font-bold text-violet-600 mt-1">{stats.total_executions}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Paused</p>
              <p className="text-2xl font-bold text-slate-400 mt-1">{(stats.total_workflows || 0) - (stats.active_workflows || 0)}</p>
            </CardContent></Card>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="workflows" data-testid="tab-workflows">Workflows</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">Execution Logs</TabsTrigger>
          </TabsList>

          <TabsContent value="workflows" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading workflows...</div>
            ) : workflows.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Workflow className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No workflows yet</p>
                  <p className="text-xs text-slate-400 mb-4">Create automated rules to save time on repetitive tasks</p>
                  <Button onClick={() => setShowCreateDialog(true)} data-testid="empty-add-workflow-btn">
                    <Plus className="h-4 w-4 mr-2" />Create First Workflow
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {workflows.map(wf => (
                  <WorkflowCard key={wf.workflow_id} workflow={wf} onToggle={handleToggle} onDelete={handleDelete} onTest={handleTest} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="logs" className="mt-4">
            {logs.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-400">No execution logs yet. Test or activate a workflow to see logs.</CardContent></Card>
            ) : (
              <div className="space-y-2" data-testid="workflow-logs">
                {logs.map(log => (
                  <div key={log.log_id} className="p-3 border border-slate-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.status === 'test' ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                        {log.status === 'test' ? <TestTube2 className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {log.trigger_type && (TRIGGER_TYPES[log.trigger_type]?.label || log.trigger_type)}
                          {' '}<ArrowRight className="h-3 w-3 inline text-slate-300" />{' '}
                          {(log.actions_executed || []).map(a => a.action).join(', ')}
                        </p>
                        <p className="text-xs text-slate-400">{new Date(log.executed_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${log.status === 'test' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {log.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <CreateWorkflowDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreated={(wf) => { setWorkflows(prev => [wf, ...prev]); fetchData(); }} templates={templates} />
      </div>
    </DashboardLayout>
  );
}
